import { Injectable } from '@angular/core';

export interface Unit {
  code: string;
  name: string;
  category: 'length' | 'area' | 'volume' | 'weight' | 'time' | 'count' | 'other';
  baseUnit?: string; // Base unit for conversion
  conversionFactor?: number; // Factor to convert to base unit
}

@Injectable({
  providedIn: 'root'
})
export class UnitConversionService {
  
  // Available units with their conversion factors
  units: Unit[] = [
    // Length units
    { code: 'm', name: 'Meter', category: 'length', baseUnit: 'm', conversionFactor: 1 },
    { code: 'km', name: 'Kilometer', category: 'length', baseUnit: 'm', conversionFactor: 1000 },
    { code: 'cm', name: 'Centimeter', category: 'length', baseUnit: 'm', conversionFactor: 0.01 },
    { code: 'mm', name: 'Millimeter', category: 'length', baseUnit: 'm', conversionFactor: 0.001 },
    { code: 'rmt', name: 'Running Meter', category: 'length', baseUnit: 'm', conversionFactor: 1 },
    { code: 'rm', name: 'Running Meter', category: 'length', baseUnit: 'm', conversionFactor: 1 },
    { code: 'ft', name: 'Feet', category: 'length', baseUnit: 'm', conversionFactor: 0.3048 },
    { code: 'in', name: 'Inch', category: 'length', baseUnit: 'm', conversionFactor: 0.0254 },
    
    // Area units
    { code: 'sqm', name: 'Square Meter', category: 'area', baseUnit: 'sqm', conversionFactor: 1 },
    // { code: 'm2', name: 'Square Meter', category: 'area', baseUnit: 'sqm', conversionFactor: 1 },
    { code: 'sqft', name: 'Square Feet', category: 'area', baseUnit: 'sqm', conversionFactor: 0.092903 },
    { code: 'acre', name: 'Acre', category: 'area', baseUnit: 'sqm', conversionFactor: 4046.86 },
    { code: 'hectare', name: 'Hectare', category: 'area', baseUnit: 'sqm', conversionFactor: 10000 },
    
    // Volume units
    { code: 'cum', name: 'Cubic Meter', category: 'volume', baseUnit: 'cum', conversionFactor: 1 },
    { code: 'm3', name: 'Cubic Meter', category: 'volume', baseUnit: 'cum', conversionFactor: 1 },
    { code: 'l', name: 'Litre', category: 'volume', baseUnit: 'cum', conversionFactor: 0.001 },
    { code: 'ltr', name: 'Litre', category: 'volume', baseUnit: 'cum', conversionFactor: 0.001 },
    { code: 'ml', name: 'Millilitre', category: 'volume', baseUnit: 'cum', conversionFactor: 0.000001 },
    { code: 'cuft', name: 'Cubic Feet', category: 'volume', baseUnit: 'cum', conversionFactor: 0.0283168 },
    { code: 'gallon', name: 'Gallon', category: 'volume', baseUnit: 'cum', conversionFactor: 0.00378541 },
    
    // Weight units
    { code: 'kg', name: 'Kilogram', category: 'weight', baseUnit: 'kg', conversionFactor: 1 },
    { code: 'g', name: 'Gram', category: 'weight', baseUnit: 'kg', conversionFactor: 0.001 },
    { code: 'mt', name: 'Metric Tonne', category: 'weight', baseUnit: 'kg', conversionFactor: 1000 },
    { code: 'ton', name: 'Tonne', category: 'weight', baseUnit: 'kg', conversionFactor: 1000 },
    { code: 'lb', name: 'Pound', category: 'weight', baseUnit: 'kg', conversionFactor: 0.453592 },
    { code: 'bag', name: 'Bag (50kg)', category: 'weight', baseUnit: 'kg', conversionFactor: 50 },
    
    // Time units
    { code: 'hr', name: 'Hour', category: 'time', baseUnit: 'hr', conversionFactor: 1 },
    { code: 'd', name: 'Day', category: 'time', baseUnit: 'hr', conversionFactor: 24 },
    { code: 'min', name: 'Minute', category: 'time', baseUnit: 'hr', conversionFactor: 1/60 },
    { code: 'week', name: 'Week', category: 'time', baseUnit: 'hr', conversionFactor: 168 },
    { code: 'month', name: 'Month', category: 'time', baseUnit: 'hr', conversionFactor: 730 },
    
    // Count units
    { code: 'nos', name: 'Numbers', category: 'count', baseUnit: 'nos', conversionFactor: 1 },
    { code: 'each', name: 'Each', category: 'count', baseUnit: 'nos', conversionFactor: 1 },
    { code: 'pcs', name: 'Pieces', category: 'count', baseUnit: 'nos', conversionFactor: 1 },
    { code: 'set', name: 'Set', category: 'count', baseUnit: 'nos', conversionFactor: 1 },
    { code: 'pair', name: 'Pair', category: 'count', baseUnit: 'nos', conversionFactor: 1 },
    
    // Other units
    { code: 'ls', name: 'Lump Sum', category: 'other', baseUnit: 'ls', conversionFactor: 1 },
    { code: 'job', name: 'Job', category: 'other', baseUnit: 'job', conversionFactor: 1 },
    { code: 'percentage', name: 'Percentage', category: 'other', baseUnit: 'percentage', conversionFactor: 1 },
  ];

  // Unit categories for different field types
  unitCategories: { [key: string]: string[] } = {
    'distance': ['m', 'km', 'cm', 'mm', 'rmt', 'rm', 'ft', 'in'],
    'area': ['sqm', 'm2', 'sqft', 'acre', 'hectare'],
    'volume': ['cum', 'm3', 'l', 'ltr', 'ml', 'cuft', 'gallon'],
    'weight': ['kg', 'g', 'mt', 'ton', 'lb', 'bag'],
    'time': ['hr', 'd', 'min', 'week', 'month'],
    'count': ['nos', 'each', 'pcs', 'set', 'pair'],
    'other': ['ls', 'job', 'percentage']
  };

  // Field type to unit category mapping
  fieldTypeMapping: { [key: string]: string } = {
    'distance': 'distance',
    'size': 'area',
    'area': 'area',
    'volume': 'volume',
    'weight': 'weight',
    'duration': 'time',
    'number': 'count',
    'percentage': 'other',
    'power': 'other',
    'pressure': 'other',
    'bandwidth': 'other',
    'coordinates': 'other'
  };

  constructor() {}

  /**
   * Get units for a specific category
   */
  getUnitsByCategory(category: string): Unit[] {
    const unitCodes = this.unitCategories[category] || [];
    return this.units.filter(u => unitCodes.includes(u.code.toLowerCase()));
  }

  /**
   * Get units for a field type
   */
  getUnitsForFieldType(fieldType: string): Unit[] {
    const category = this.fieldTypeMapping[fieldType] || 'other';
    return this.getUnitsByCategory(category);
  }

  /**
   * Convert value from one unit to another
   */
  convert(value: number, fromUnit: string, toUnit: string): number | null {
    if (!value || value === 0) return value;
    
    const from = this.units.find(u => u.code.toLowerCase() === fromUnit.toLowerCase());
    const to = this.units.find(u => u.code.toLowerCase() === toUnit.toLowerCase());
    
    if (!from || !to) {
      console.warn(`Unit not found: from=${fromUnit}, to=${toUnit}`);
      return null;
    }
    
    // If same category, convert
    if (from.category === to.category && from.baseUnit === to.baseUnit) {
      // Convert to base unit first, then to target unit
      const valueInBaseUnit = value * (from.conversionFactor || 1);
      return valueInBaseUnit / (to.conversionFactor || 1);
    }
    
    // Cannot convert between different categories
    console.warn(`Cannot convert from ${from.category} to ${to.category}`);
    return null;
  }

  /**
   * Format unit display name
   */
  getUnitDisplayName(unitCode: string): string {
    const unit = this.units.find(u => u.code.toLowerCase() === unitCode.toLowerCase());
    return unit ? unit.name : unitCode.toUpperCase();
  }

  /**
   * Get all available units
   */
  getAllUnits(): Unit[] {
    return this.units;
  }

  /**
   * Check if two units are compatible (same category)
   */
  areUnitsCompatible(unit1: string, unit2: string): boolean {
    const u1 = this.units.find(u => u.code.toLowerCase() === unit1.toLowerCase());
    const u2 = this.units.find(u => u.code.toLowerCase() === unit2.toLowerCase());
    
    if (!u1 || !u2) return false;
    return u1.category === u2.category && u1.baseUnit === u2.baseUnit;
  }

  /**
   * Get standard unit for a category (for storage)
   */
  getStandardUnit(category: string): string {
    const standards: { [key: string]: string } = {
      'length': 'm',
      'area': 'sqm',
      'volume': 'cum',
      'weight': 'kg',
      'time': 'hr',
      'count': 'nos',
      'other': 'ls'
    };
    return standards[category] || 'nos';
  }
}

